import React, { useEffect, useState } from "react";
import {
    TableRow,
    TableCell,
    Collapse,
    Button,
    Typography,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    Paper,
    Checkbox,
    Box,
    Grid,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon, Edit } from "@mui/icons-material";
import { RoleWithStats, RolePermission } from "../../../../../models/DB";
import { roleManagementStore } from "../../../../../app/stores/RoleManagement";
import { RoleManagementService } from "../../../../../services/supabase/RoleManagementService";
import toastService from "../../../../../services/toast/ToastService";
import { PermissionWrapper } from "../../../../../components/PermissionWrapper";

interface PermissionEditorProps {
    role: RoleWithStats;
    open: boolean;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
}

export const PermissionEditor: React.FC<PermissionEditorProps> = ({
    role,
    open,
    isEditing,
    setIsEditing
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { rolePermissions, isLoading } = roleManagementStore();

    const [permissions, setPermissions] = useState<RolePermission[]>([]);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (role && open) {
            // Call service directly to avoid infinite loops  
            RoleManagementService().getRolePermissions(role.roleId);
        }
    }, [role?.roleId, open]); // Remove service function from dependencies

    useEffect(() => {
        setPermissions(rolePermissions);
        setHasChanges(false);
    }, [rolePermissions]);

    const handlePermissionChange = (
        screenId: string,
        permissionType: "canView" | "canCreate" | "canUpdate" | "canDelete",
        value: boolean
    ) => {
        const updatedPermissions = permissions.map(p =>
            p.screenId === screenId
                ? { ...p, [permissionType]: value }
                : p
        );

        // If enabling create/update/delete, automatically enable view
        if (value && permissionType !== "canView") {
            const permission = updatedPermissions.find(p => p.screenId === screenId);
            if (permission && !permission.canView) {
                permission.canView = true;
            }
        }

        // If disabling view, disable all other permissions
        if (!value && permissionType === "canView") {
            const permission = updatedPermissions.find(p => p.screenId === screenId);
            if (permission) {
                permission.canCreate = false;
                permission.canUpdate = false;
                permission.canDelete = false;
            }
        }

        setPermissions(updatedPermissions);
        setHasChanges(true);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!role) return;

        const success = await RoleManagementService().updateRolePermissions(role.roleId, permissions);
        if (success) {
            setHasChanges(false);
            setIsEditing(false);
            toastService.showSuccess("Permissions updated successfully.");
        }
    };

    const handleCancel = () => {
        setPermissions(rolePermissions);
        setHasChanges(false);
        setIsEditing(false);
    };

    const groupedScreens = permissions.reduce((acc, screen) => {
        const category = screen.category || "Other";
        if (!acc[category]) acc[category] = [];
        acc[category].push(screen);
        return acc;
    }, {} as Record<string, RolePermission[]>);

    return (
        <TableRow>
            <TableCell
                style={{
                    paddingBottom: 0,
                    paddingTop: 0,
                    backgroundColor: theme.palette.mode === "dark"
                        ? theme.palette.background.default
                        : theme.palette.grey[100]
                }}
                colSpan={6}
            >
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ marginX: { xs: 1, sm: 2 }, marginY: { xs: 2, sm: 4 }, padding: { xs: 1, sm: 2 } }}>
                        {/* Header with buttons */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: 2,
                            mb: 3 
                        }}>
                            <Box>
                                <Typography
                                    variant={isMobile ? 'subtitle1' : 'h6'}
                                    gutterBottom
                                    component="div"
                                    sx={{
                                        fontFamily: "primaryFont",
                                        letterSpacing: 0.5,
                                        fontWeight: "bold"
                                    }}
                                >
                                    Edit Permissions {role?.roleName ? `- ${role?.roleName}` : ""}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                                    {isMobile 
                                        ? 'Configure role permissions below.'
                                        : 'Configure what this role can access and do in each screen. View access is required for other permissions.'
                                    }
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                                {isEditing ? (
                                    <>
                                        <Button
                                            onClick={handleSave}
                                            variant="contained"
                                            color="success"
                                            startIcon={<SaveIcon />}
                                            disabled={!hasChanges || isLoading}
                                            sx={{ height: 36, fontSize: 12, px: 2 }}
                                        >
                                            {isLoading ? "Saving..." : "Save"}
                                        </Button>
                                        <Button
                                            onClick={handleCancel}
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            disabled={isLoading}
                                            sx={{ 
                                                height: 36, 
                                                fontSize: 12, 
                                                px: 2,
                                                borderColor: theme.palette.divider,
                                                backgroundColor: theme.palette.background.paper
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <PermissionWrapper screen="roles" permission="update">
                                        <Button
                                            onClick={handleEdit}
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            sx={{
                                                height: 36,
                                                fontSize: 12,
                                                px: 2,
                                                borderColor: theme.palette.primary.main,
                                                backgroundColor: theme.palette.background.paper,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.main + '10'
                                                }
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </PermissionWrapper>
                                )}
                            </Box>
                        </Box>

                        {isLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ opacity: isEditing ? 1 : 0.7 }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: theme.palette.divider }}>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                                                        Screen
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 } }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: 10, sm: 14 } }}>
                                                        {isMobile ? 'V' : 'View'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 } }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: 10, sm: 14 } }}>
                                                        {isMobile ? 'C' : 'Create'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 } }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: 10, sm: 14 } }}>
                                                        {isMobile ? 'U' : 'Update'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ px: { xs: 0.5, sm: 2 } }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: 10, sm: 14 } }}>
                                                        {isMobile ? 'D' : 'Delete'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(groupedScreens).map(([category, screenList]) => (
                                                <React.Fragment key={category}>
                                                    {/* Screen Permission Rows */}
                                                    {screenList.map((screen) => (
                                                        <TableRow key={screen.screenId} hover>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                                                                    {screen.displayName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ px: { xs: 0, sm: 2 } }}>
                                                                <Checkbox
                                                                    checked={screen.canView}
                                                                    onChange={(e) => handlePermissionChange(
                                                                        screen.screenId,
                                                                        "canView",
                                                                        e.target.checked
                                                                    )}
                                                                    disabled={!isEditing}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ p: { xs: 0.25, sm: 1 } }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ px: { xs: 0, sm: 2 } }}>
                                                                <Checkbox
                                                                    checked={screen.canCreate}
                                                                    onChange={(e) => handlePermissionChange(
                                                                        screen.screenId,
                                                                        "canCreate",
                                                                        e.target.checked
                                                                    )}
                                                                    disabled={!screen.canView || !isEditing}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ p: { xs: 0.25, sm: 1 } }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ px: { xs: 0, sm: 2 } }}>
                                                                <Checkbox
                                                                    checked={screen.canUpdate}
                                                                    onChange={(e) => handlePermissionChange(
                                                                        screen.screenId,
                                                                        "canUpdate",
                                                                        e.target.checked
                                                                    )}
                                                                    disabled={!screen.canView || !isEditing}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ p: { xs: 0.25, sm: 1 } }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ px: { xs: 0, sm: 2 } }}>
                                                                <Checkbox
                                                                    checked={screen.canDelete}
                                                                    onChange={(e) => handlePermissionChange(
                                                                        screen.screenId,
                                                                        "canDelete",
                                                                        e.target.checked
                                                                    )}
                                                                    disabled={!screen.canView || !isEditing}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ p: { xs: 0.25, sm: 1 } }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
}; 